/**
 * Was Dr. Roskopf wann sagt.
 *
 * Die Regeln des Spiels sind nicht schwer, aber sie sind unsichtbar: dass ein
 * Zeitfenster nur geschaetzt ist, dass die Fahrtstufe eine Abwaegung und keine
 * Rechnung ist, dass sich der Fall durch reines Retten nicht loesen laesst.
 * Nichts davon erklaert sich beim Zusehen.
 *
 * Deshalb begleitet die Institutsleitung die erste Mission mit ein paar
 * Einwuerfen und bleibt danach auf Abruf. Zwei Entwurfsentscheidungen halten
 * das ertraeglich:
 *
 *  * **Ein Einwurf je Ort, hoechstens einmal.** Wer eine Szene zum zweiten Mal
 *    sieht, hat sie verstanden.
 *  * **Nichts blockiert.** Die Einblendung laesst sich wegklicken, und wer
 *    das Tutorial abschaltet, hoert nichts mehr davon.
 *
 * Das Modul kennt weder Phaser noch Bilddateien — nur Reihenfolge, Zustand
 * und Textschluessel. Damit ist die Logik ohne Browser pruefbar.
 */

/** Die Orte, an denen sie sich meldet — in genau dieser Reihenfolge. */
export const TUTORIAL_STEPS = [
  /** An der Station: Was ein Notruf ist und warum die Stufe nur eine Schaetzung ist. */
  'calls',
  /** In der Einsatzplanung: Fahrzeug, Fahrtstufe, Tiefgang. */
  'briefing',
  /** Auf See: dass unterwegs Entscheidungen anstehen, die Zeit kosten. */
  'voyage',
  /** Am Einsatzort: dass die erreichte Stufe bestimmt, *welche* Art Fund es gibt. */
  'site',
  /** In der Beweislage: dass die Station nicht auswertet. */
  'evidence',
] as const;

export type TutorialStep = (typeof TUTORIAL_STEPS)[number];

export interface TutorialState {
  /** Ob ueberhaupt noch Einwuerfe kommen. */
  enabled: boolean;
  /** Bereits gezeigte Schritte. */
  readonly seen: TutorialStep[];
}

export function createTutorialState(): TutorialState {
  return { enabled: true, seen: [] };
}

/**
 * Faellt an diesem Ort ein Einwurf an?
 *
 * Streng der Reihe nach: Wer die Beweislage vor der ersten Fahrt oeffnet,
 * bekommt dort noch nichts zu hoeren — sonst stuende die Erklaerung der
 * Fundarten vor der ersten Bergung und erklaerte nichts.
 */
export function pendingStep(state: TutorialState, at: TutorialStep): TutorialStep | null {
  if (!state.enabled) return null;
  if (state.seen.includes(at)) return null;

  const position = TUTORIAL_STEPS.indexOf(at);
  for (let i = 0; i < position; i++) {
    const earlier = TUTORIAL_STEPS[i];
    if (earlier !== undefined && !state.seen.includes(earlier)) return null;
  }

  return at;
}

/** Haelt fest, dass ein Schritt gezeigt wurde. */
export function markSeen(state: TutorialState, step: TutorialStep): void {
  if (!state.seen.includes(step)) state.seen.push(step);
}

/**
 * Hakt alle Schritte vor diesem ab.
 *
 * Noetig, weil nicht jeder Schritt zwangslaeufig vorkommt: Auf einer kurzen
 * Ueberfahrt gibt es keine Ereignisse, also auch keinen Ereignis-Einwurf.
 * Ohne dieses Nachziehen bliebe die Einweisung dort stecken und die spaeteren
 * Erklaerungen kaemen nie — die Reihenfolge wuerde zur Sackgasse statt zur
 * Reihenfolge.
 */
export function catchUpTo(state: TutorialState, step: TutorialStep): void {
  const position = TUTORIAL_STEPS.indexOf(step);
  for (let i = 0; i < position; i++) {
    const earlier = TUTORIAL_STEPS[i];
    if (earlier !== undefined) markSeen(state, earlier);
  }
}

/** Sind alle Einwuerfe durch? Danach meldet sie sich nur noch auf Abruf. */
export function isComplete(state: TutorialState): boolean {
  return TUTORIAL_STEPS.every((step) => state.seen.includes(step));
}

/** Textschluessel des Einwurfs. */
export function stepTextKey(step: TutorialStep): string {
  return `adviser.step.${step}`;
}

/**
 * Die Orte, an denen sie auf Abruf etwas zu sagen hat.
 *
 * Bewusst dieselben Kennungen wie die Tutorialschritte plus die Karte: Der
 * Hinweis auf Abruf ist die ausfuehrlichere Fassung desselben Themas, nicht
 * ein zweiter Textbestand, der auseinanderlaufen koennte.
 */
export const HINT_TOPICS = [...TUTORIAL_STEPS, 'map'] as const;

export type HintTopic = (typeof HINT_TOPICS)[number];

export function hintTextKey(topic: HintTopic): string {
  return `adviser.hint.${topic}`;
}
